import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Sparkle, Plus, PencilSimple, Trash, X, Wrench, Warning, Calendar, Certificate } from "@phosphor-icons/react";

const FREQ_LABEL = {
  mensual: "Mensual",
  trimestral: "Trimestral",
  semestral: "Semestral",
  anual: "Anual",
};

function TechSheetView({ sheet }) {
  if (!sheet) return null;
  const routines = sheet.preventive_routines || [];
  const failures = sheet.failure_points || [];
  const standards = sheet.standards || [];
  const lifespan = sheet.estimated_lifespan_years;
  return (
    <div className="grid gap-3 text-sm">
      {routines.length > 0 && (
        <div>
          <div className="q-label text-[color:var(--q-muted)] mb-1 flex items-center gap-1"><Calendar size={12}/> Rutinas preventivas</div>
          <div className="grid gap-2">
            {routines.map((r, i) => (
              <div key={i} className="rounded-lg border border-[color:var(--q-border)] bg-white p-2">
                <div className="text-xs font-semibold text-[color:var(--q-navy)] mb-1">{FREQ_LABEL[r.frequency] || r.frequency}</div>
                <ul className="list-disc list-inside text-xs space-y-0.5">
                  {(r.tasks || []).map((t, j) => <li key={j}>{t}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
      {failures.length > 0 && (
        <div>
          <div className="q-label text-[color:var(--q-muted)] mb-1 flex items-center gap-1"><Warning size={12}/> Puntos comunes de falla</div>
          <ul className="list-disc list-inside text-xs space-y-0.5">
            {failures.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        {lifespan != null && (
          <div>
            <div className="q-label text-[color:var(--q-muted)] mb-1">Vida útil estimada</div>
            <div className="font-mono font-semibold text-lg text-[color:var(--q-navy)]">{lifespan} años</div>
          </div>
        )}
        {standards.length > 0 && (
          <div>
            <div className="q-label text-[color:var(--q-muted)] mb-1 flex items-center gap-1"><Certificate size={12}/> Normas / estándares</div>
            <ul className="text-xs space-y-0.5">
              {standards.map((s, i) => <li key={i}>· {s}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function AssetEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    category: initial?.category || "",
    brand: initial?.brand || "",
    model: initial?.model || "",
    preventive_days: initial?.preventive_days || "",
    tech_sheet: initial?.tech_sheet || null,
  }));
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editSheetJson, setEditSheetJson] = useState(false);
  const [sheetDraft, setSheetDraft] = useState(() => JSON.stringify(initial?.tech_sheet || {}, null, 2));

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!form.name.trim()) { toast.error("Escribe el nombre del activo primero"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/asset-sheet", {
        name: form.name, category: form.category || null,
        brand: form.brand || null, model: form.model || null,
      });
      setForm((f) => ({ ...f, tech_sheet: data.tech_sheet }));
      setSheetDraft(JSON.stringify(data.tech_sheet, null, 2));
      toast.success("Ficha generada por QUILLA IA · revísala antes de guardar");
    } catch (e) {
      toast.error(e.response?.data?.detail || "La IA no pudo generar la ficha. Reintenta.");
    } finally { setGenerating(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      let sheet = form.tech_sheet;
      if (editSheetJson) {
        try { sheet = JSON.parse(sheetDraft); }
        catch { toast.error("La ficha técnica no es JSON válido"); setSaving(false); return; }
      }
      const payload = {
        name: form.name.trim(),
        category: form.category || null,
        brand: form.brand || null,
        model: form.model || null,
        preventive_days: form.preventive_days ? parseInt(form.preventive_days) : null,
        tech_sheet: sheet || null,
      };
      if (initial?.id) {
        await api.patch(`/assets/${initial.id}`, payload);
        toast.success("Activo actualizado");
      } else {
        await api.post("/assets", payload);
        toast.success("Activo creado");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div className="q-card w-full max-w-2xl max-h-[92vh] overflow-y-auto p-5 rounded-t-2xl md:rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="asset-editor">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">{initial?.id ? "Editar activo" : "Nuevo activo"}</h3>
            <p className="text-xs text-[color:var(--q-muted)]">Precios los edita Consola QUILLA. Aquí gestionas la ficha operativa.</p>
          </div>
          <button onClick={onClose} className="text-[color:var(--q-muted)] hover:text-[color:var(--q-navy)]"><X size={18}/></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="col-span-2">
            <label className="q-label block mb-1">Nombre del activo</label>
            <input data-testid="asset-name-input" value={form.name} onChange={(e) => setField("name", e.target.value)}
                   placeholder="Ej: Bomba de piscina 3HP"
                   className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)]"/>
          </div>
          <div>
            <label className="q-label block mb-1">Categoría</label>
            <input value={form.category} onChange={(e) => setField("category", e.target.value)}
                   placeholder="climatización, piscinas, contraincendios…"
                   className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)]"/>
          </div>
          <div>
            <label className="q-label block mb-1">Días entre preventivos</label>
            <input type="number" value={form.preventive_days} onChange={(e) => setField("preventive_days", e.target.value)}
                   className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono"/>
          </div>
          <div>
            <label className="q-label block mb-1">Marca (opcional)</label>
            <input value={form.brand} onChange={(e) => setField("brand", e.target.value)}
                   className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)]"/>
          </div>
          <div>
            <label className="q-label block mb-1">Modelo (opcional)</label>
            <input value={form.model} onChange={(e) => setField("model", e.target.value)}
                   className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)]"/>
          </div>
        </div>

        <div className="rounded-lg border-2 border-dashed border-[color:var(--q-gold)]/40 bg-[color:var(--q-gold)]/5 p-4 mb-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <div className="font-semibold text-sm flex items-center gap-2">
                <Sparkle size={14} weight="fill" className="text-[color:var(--q-gold)]"/>
                Ficha técnica con QUILLA IA
              </div>
              <p className="text-xs text-[color:var(--q-muted)]">Rutinas preventivas, fallas típicas, vida útil y normas. Revísala antes de guardar.</p>
            </div>
            <button onClick={generate} disabled={generating || !form.name.trim()}
                    data-testid="generate-tech-sheet-btn"
                    className="q-btn-gold whitespace-nowrap flex items-center gap-2">
              <Sparkle size={14} weight="fill"/>
              {generating ? "Generando…" : (form.tech_sheet ? "Regenerar" : "Generar ficha")}
            </button>
          </div>
          {form.tech_sheet ? (
            <div className="mt-3">
              {!editSheetJson ? (
                <TechSheetView sheet={form.tech_sheet}/>
              ) : (
                <textarea value={sheetDraft} onChange={(e) => setSheetDraft(e.target.value)}
                          className="w-full h-64 p-2 rounded border font-mono text-[11px]" data-testid="asset-sheet-json"/>
              )}
              <button onClick={() => {
                if (!editSheetJson) setSheetDraft(JSON.stringify(form.tech_sheet, null, 2));
                setEditSheetJson(!editSheetJson);
              }} className="text-xs text-[color:var(--q-navy)] underline mt-2">
                {editSheetJson ? "Vista normal" : "Editar como JSON"}
              </button>
            </div>
          ) : (
            <div className="text-xs text-[color:var(--q-muted)] italic mt-2">Aún sin ficha técnica.</div>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <button className="q-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="q-btn-primary" onClick={save} data-testid="asset-save-btn" disabled={saving || generating}>
            {saving ? "Guardando…" : (generating ? "Espera a la ficha…" : "Guardar activo")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CatalogTab() {
  const [assets, setAssets] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => api.get("/assets").then(r => setAssets(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const del = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.name}" del catálogo?`)) return;
    try { await api.delete(`/assets/${a.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h3 className="text-lg font-semibold">Catálogo de activos</h3>
          <p className="text-xs text-[color:var(--q-muted)]">Cada activo puede tener una ficha técnica generada por IA (rutinas, fallas, vida útil, normas).</p>
        </div>
        <button data-testid="new-asset-btn" onClick={() => setCreating(true)} className="q-btn-primary flex items-center gap-2 whitespace-nowrap">
          <Plus size={14} weight="bold"/> Nuevo activo
        </button>
      </div>

      <div className="grid gap-3">
        {assets.length === 0 && (
          <div className="q-card p-6 text-center text-sm text-[color:var(--q-muted)]">
            Aún no hay activos en el catálogo. Crea el primero para empezar a acumular fichas técnicas.
          </div>
        )}
        {assets.map(a => (
          <div key={a.id} className="q-card p-4" data-testid={`asset-row-${a.id}`}>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-[color:var(--q-navy)] text-[color:var(--q-gold)] flex items-center justify-center shrink-0">
                <Wrench size={18} weight="fill"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                  {a.name}
                  {a.tech_sheet && <span className="chip chip-media text-[10px] flex items-center gap-1"><Sparkle size={9} weight="fill"/> Ficha IA</span>}
                </div>
                <div className="text-xs text-[color:var(--q-muted)] mt-0.5">
                  {a.category || "sin categoría"}
                  {a.brand && ` · ${a.brand}`}
                  {a.model && ` ${a.model}`}
                  {a.preventive_days ? ` · preventivo cada ${a.preventive_days} días` : ""}
                </div>
                <div className="text-xs mt-1 text-[color:var(--q-muted)] font-mono">
                  Rep. US${(a.repair_price || 0).toFixed(2)} · Reempl. US${(a.replace_price || 0).toFixed(2)}
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-1 shrink-0">
                {a.tech_sheet && (
                  <button onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                          className="text-xs text-[color:var(--q-navy)] hover:underline px-2">
                    {expanded === a.id ? "Ocultar ficha" : "Ver ficha"}
                  </button>
                )}
                <button onClick={() => setEditing(a)} data-testid={`edit-asset-${a.id}`}
                        className="text-xs text-[color:var(--q-navy)] hover:underline flex items-center gap-1 px-2">
                  <PencilSimple size={12}/> Editar
                </button>
                <button onClick={() => del(a)}
                        className="text-xs text-red-500 hover:underline flex items-center gap-1 px-2">
                  <Trash size={12}/> Borrar
                </button>
              </div>
            </div>
            {expanded === a.id && a.tech_sheet && (
              <div className="mt-4 pt-4 border-t border-[color:var(--q-border)]">
                <TechSheetView sheet={a.tech_sheet}/>
              </div>
            )}
          </div>
        ))}
      </div>

      {creating && <AssetEditor onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }}/>}
      {editing && <AssetEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}
